import {
  AVG_NEW_ACCOUNTS_DISCOUNT,
  AVG_NEW_COMPANIES_DISCOUNT,
  DISCOUNTED_FIRST_PAYMENT_RATE,
  DISCOUNTED_NEW_ACCOUNTS,
  DISCOUNTED_NEW_COMPANIES,
  DISCOUNT_VALUE,
  DISPUTE_RATE,
  MRR_DISCOUNT_AMOUNT,
  REFUND_RATE,
} from "./exampleData";
import { MUTED } from "./chartTheme";
import MetricBarChart from "./MetricBarChart";
import AmountBarChart from "./AmountBarChart";

const pct = (v: number) => `${v}%`;
const count = (v: number) => `${v}`;

export const RefundRateChart = () => (
  <MetricBarChart
    caption="Refund rate holds around 2% of gross billings — refunds and credit memos as a share of what you billed. A creeping refund rate is often an early sign of a product, expectations or quality gap."
    data={REFUND_RATE}
    seriesName="Refund rate"
    format={pct}
    color={MUTED}
    domain={[0, "dataMax + 1"]}
  />
);

export const DisputeRateChart = () => (
  <MetricBarChart
    caption="Dispute rate sits below 1% — chargebacks as a share of gross billings. Crossing the 1% line risks card-network penalties and higher processing costs, so the ceiling here is a hard operational limit, not a soft target."
    data={DISPUTE_RATE}
    seriesName="Dispute rate"
    format={pct}
    color={MUTED}
    domain={[0, 1.5]}
    reference={{ y: 1, label: "1% penalty risk" }}
  />
);

export const MrrDiscountAmountChart = () => (
  <AmountBarChart
    caption="Total MRR given away in discounts runs $1,200–$1,500 a month, normalised to a monthly figure. It is real revenue you chose not to collect — worth watching as carefully as the revenue you did."
    data={MRR_DISCOUNT_AMOUNT}
    seriesName="MRR discount"
    color={MUTED}
  />
);

export const DiscountValueChart = () => (
  <AmountBarChart
    caption="Discount value on first payments of new customers runs $600–$900 a month — the up-front cost of the discounts used to win new business. It is acquisition spend by another name."
    data={DISCOUNT_VALUE}
    seriesName="Discount value"
    color={MUTED}
  />
);

export const DiscountedFirstPaymentRateChart = () => (
  <MetricBarChart
    caption="The share of new customers whose first payment was discounted falls from 45% to 38%. A high, sticky figure suggests you are leaning on price to close rather than on value."
    data={DISCOUNTED_FIRST_PAYMENT_RATE}
    seriesName="Discounted first payments"
    format={pct}
    color={MUTED}
    domain={[0, 100]}
  />
);

export const DiscountedNewAccountsChart = () => (
  <MetricBarChart
    caption="Discounted new accounts run 7–10 a month — the count of new customers who needed a discount to sign. Reading it beside total new accounts shows how much of acquisition depends on price concessions."
    data={DISCOUNTED_NEW_ACCOUNTS}
    seriesName="Discounted new accounts"
    format={count}
    color={MUTED}
    domain={[0, "dataMax + 3"]}
    yWidth={28}
  />
);

export const DiscountedNewCompaniesChart = () => (
  <MetricBarChart
    caption="Discounted new companies run 4–6 a month — the company-level view of discount-driven acquisition. Fewer but larger, so each discount here typically carries more revenue weight."
    data={DISCOUNTED_NEW_COMPANIES}
    seriesName="Discounted new companies"
    format={count}
    color={MUTED}
    domain={[0, "dataMax + 3"]}
    yWidth={28}
  />
);

export const AvgNewAccountsDiscountChart = () => (
  <AmountBarChart
    caption="The average discount per discounted new account swings between $65 and $110. A rising average means you are conceding more per deal to win it — a signal to check pricing and sales incentives."
    data={AVG_NEW_ACCOUNTS_DISCOUNT}
    seriesName="Avg discount"
    color={MUTED}
  />
);

export const AvgNewCompaniesDiscountChart = () => (
  <AmountBarChart
    caption="The average discount per discounted new company runs $120–$170 — larger than the account average, as company deals are bigger. Watch it against average sales price to see how deep the concessions really cut."
    data={AVG_NEW_COMPANIES_DISCOUNT}
    seriesName="Avg discount"
    color={MUTED}
  />
);
